import { AxiosError } from 'axios'
import { cloneDeep } from 'lodash'
import { useCallback } from 'react'
import { useIsMutating, useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import queryClient from '~/data/queryClient'
import { QueueFile, useRecordClassificationQueueStore } from '~/stores/record-classification.store'
import { dateComparator } from '~/utils/comparators/dateComparator'
import { IRecordFile, IRecordFileClassificationHistoryRecord, RECORD_OCR_STATUS } from '~/utils/types'
import {
  GenerateFilenameProps,
  IMoveRecordFileProps,
  TRawRecordFileResponse,
  UpdateRecordFileRequest,
  UploadFileParams,
  confirmClassification,
  deleteRecordFile,
  fillRecordFileWithAi,
  generateFilename,
  getPendingRecordFiles,
  getRecordFile,
  getRecordFiles,
  getRecordFilesByClassification,
  getRecordFilesNotClassified,
  moveCategoryFromRecordFile,
  moveRecordFile,
  updateRecordFile,
  uploadRecordFile,
  uploadRecordFileNotClassified,
} from './api'

export const useGetRecordFiles = (reportId: string, categoryId?: string) => {
  return useQuery({
    queryKey: ['get_record_files', reportId, categoryId],
    queryFn: () => getRecordFiles(reportId, categoryId ?? ''),
    refetchInterval: (data) => {
      if (!data) return false
      const hasPendingOcr = data.some((file) => {
        const ocrStatus = file.ocr_status
        return (
          ocrStatus &&
          ![RECORD_OCR_STATUS.ERROR, RECORD_OCR_STATUS.EXTRACTION_COMPLETE].includes(ocrStatus)
        )
      })
      return hasPendingOcr ? 10000 : false
    }
  })
}

export const useGetRecordFilesWithMultipleCategories = (
  reportId: string,
  categoriesIds: string[] = [],
) => {
  const isEnabled = !!reportId && categoriesIds.length > 0
  return useQueries(
    categoriesIds.map((catId) => ({
      queryKey: ['get_record_files', reportId, catId],
      queryFn: () => getRecordFiles(reportId, catId ?? ''),
      enabled: isEnabled,
    })),
  )
}

export const useGetRecordFilesByClassification = (reportId: string, document_type: string) => {
  return useQuery({
    queryKey: ['get_record_files_by_classification', reportId, document_type],
    queryFn: () => getRecordFilesByClassification(reportId, document_type),
  })
}

export const useGetRecordFilesNotClassified = (reportId: string) => {
  return useQuery({
    queryKey: ['get_record_files_not_classified', reportId],
    queryFn: () => getRecordFilesNotClassified(reportId),
    enabled: !!reportId,
  })
}

export const useGetRecordFile = () => {
  return useMutation(({ reportId, recordFileId }: { reportId: string; recordFileId: string }) =>
    getRecordFile(reportId, recordFileId),
  )
}

export const useGenerateFilename = () => {
  return useMutation((params: GenerateFilenameProps) => generateFilename(params), {
    onSuccess: (response) => {
      const queryKey = ['get_record_files', response.report, response.record_category_id]
      const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
      const updatedContent =
        recordFiles?.map((el) => {
          if (el?._id === response?._id) return response
          return el
        }) ?? []
      queryClient.setQueryData(queryKey, updatedContent)
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      console.error('Error while changing record file name', error)
      toast.error(error?.response?.data?.message)
    },
  })
}

type UpdateRecordFileProps = {
  fileId: string
  updateData: Partial<UpdateRecordFileRequest>
  reportId: string
  recordCategoryId: string
}

export const useUpdateRecordFile = () => {
  return useMutation(
    ({ fileId, updateData, reportId }: UpdateRecordFileProps) =>
      updateRecordFile(reportId, fileId, updateData),
    {
      onSuccess: (response, params) => {
        const queryKey = ['get_record_files', params.reportId, params.recordCategoryId]
        const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
        const updatedContent =
          recordFiles?.map((el) => {
            if (el?._id === response?._id) return response
            return el
          }) ?? []
        queryClient.setQueryData(queryKey, updatedContent)
      },
    },
  )
}

type useMoveCategoryFromRecordFileProps = {
  fileId: string
  reportId: string
  recordCategoryId?: string
  status_classification?: string
}

export const useMoveCategoryFromRecordFile = () => {
  return useMutation(
    ({ fileId, recordCategoryId, status_classification }: useMoveCategoryFromRecordFileProps) =>
      moveCategoryFromRecordFile(fileId, recordCategoryId, status_classification),
    {
      onSuccess: (response, params) => {
        const queryKey = ['get_record_files', params.reportId, params.recordCategoryId]
        const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
        const updatedContent =
          recordFiles?.map((el) => {
            if (el?._id === response?._id) return response
            return el
          }) ?? []
        queryClient.setQueryData(queryKey, updatedContent)
      },
    },
  )
}

interface IUseMoveRecordFileProps extends IMoveRecordFileProps {
  currentRecordCategoryUUID?: string
  currentReportId?: string
}

export const useMoveRecordFile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['move_record_file'],
    mutationFn: (props: IUseMoveRecordFileProps) => moveRecordFile(props),
    onSuccess: (data, variables) => {
      const reportId = data.report
      const recordFileId = variables.recordFileId
      const newRecordCategoryUUID = variables.recordCategoryUUID

      if (!reportId) return

      // Remove from current record category
      if (variables.currentRecordCategoryUUID) {
        queryClient.setQueryData(
          ['get_record_files', reportId, variables.currentRecordCategoryUUID],
          (oldData: unknown) => {
            if (!oldData) return oldData

            const newData = cloneDeep(oldData as TRawRecordFileResponse[]).filter(
              (i) => i._id !== recordFileId,
            )

            return newData
          },
        )
      }

      // Remove from pending files
      queryClient.setQueryData(['pending_record_files', reportId], (oldData: unknown) => {
        if (!oldData) return oldData

        const newData = cloneDeep(oldData as TRawRecordFileResponse[]).filter(
          (i) => i._id !== recordFileId,
        )

        return newData
      })

      // Add into new record category
      queryClient.setQueryData(
        ['get_record_files', reportId, newRecordCategoryUUID],
        (oldData: unknown) => {
          if (!oldData) return oldData

          const newData = [...cloneDeep(oldData as TRawRecordFileResponse[]), data]

          return newData
        },
      )
    },
  })
}

export const useConfirmClassification = () => {
  return useMutation((reportId: string) => confirmClassification(reportId), {
    onMutate: () => {
      const toastId = toast.loading('Confirming classification...')
      return { toastId }
    },
    onSuccess: (_, __, context) => {
      toast.update(context?.toastId ?? '', {
        render: 'Operation completed successfully!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 3000,
      })
      queryClient.invalidateQueries(['get_record_files_not_classified'])
    },
    onError: (error: AxiosError<{ message?: string }>, _, context) => {
      console.error('Failed to confirm classification: ', error)
      toast.update(context?.toastId ?? '', {
        render: `Error while confirming classification. Please try again later.`,
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
      })
    },
  })
}

type DeleteRecordFileProps = {
  recordFileId: string
  reportId: string
  recordCategoryId: string
}

export const useDeleteRecordFile = () => {
  return useMutation(
    ({ recordFileId, reportId }: DeleteRecordFileProps) => deleteRecordFile(reportId, recordFileId),
    {
      onSuccess: (_response, params) => {
        const queryKey = ['get_record_files', params.reportId, params.recordCategoryId]
        const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
        const updatedContent =
          recordFiles?.filter((el) => {
            if (el?._id === params.recordFileId) return false
            return true
          }) ?? []
        queryClient.setQueryData(queryKey, updatedContent)
      },
    },
  )
}

export const useUploadRecordFile = () => {
  return useMutation((params: UploadFileParams) => uploadRecordFile(params), {
    onSuccess: (response, params) => {
      const queryKey = ['get_record_files', params.report_id, params.record_category_id]
      const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
      const updatedContent = [...recordFiles, response]
      queryClient.setQueryData(queryKey, updatedContent)
    },
    onError: (error, variables) => {
      console.error('Failed to upload file: ', variables.file?.name, error)
      toast.error(`Error while uploading ${variables.file?.name}. Please try again later.`, {
        autoClose: 5000,
      })
    },
  })
}

export const useUploadRecordFileNotClassified = () => {
  return useMutation((params: UploadFileParams) => uploadRecordFileNotClassified(params), {
    onSuccess: (response, params) => {
      const queryKey = ['get_record_files_not_classified', params.report_id]
      const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
      const updatedContent = [...recordFiles, response]
      queryClient.setQueryData(queryKey, updatedContent)
    },
  })
}

export const useFillRecordFileWithAi = (reportId: string, recordFileId: string) => {
  const numMutating = useIsMutating({
    mutationKey: ['fill_data_with_ai', recordFileId],
    fetching: true,
    exact: true,
  })
  const mutation = useMutation(() => fillRecordFileWithAi(reportId, recordFileId), {
    mutationKey: ['fill_data_with_ai', recordFileId],
    onMutate: () => {
      const toastId = toast.loading('Generating ZEH comments...')
      return { toastId }
    },
    onSuccess: (response, _, context) => {
      const queryKey = ['get_record_files', response?.report, response?.record_category_id]
      const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
      const updatedContent =
        recordFiles?.map((el) => {
          if (el?._id === response?._id) return response
          return el
        }) ?? []
      queryClient.setQueryData(queryKey, updatedContent)

      toast.update(context?.toastId ?? '', {
        // render: 'ZEH comments generated successfully!',
        render: 'Zeh is analyzing the document and generating comments. This process may take a few moments, please wait...',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 3000,
      })
    },
    onError: (error: AxiosError<{ message?: string }>, _, context) => {
      toast.update(context?.toastId ?? '', {
        render: error?.response?.data?.message ?? 'Error while loading ZEH comments.',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
      })
    },
  })
  return {
    ...mutation,
    isLoading: numMutating > 0,
  }
}

export const useFillRecordFileWithAiAutoAdd = () => {
  return useMutation(
    ({ recordFileId, reportId }: { recordFileId: string; reportId: string }) =>
      fillRecordFileWithAi(reportId, recordFileId, true),
    {
      mutationKey: ['fill_data_with_ai'],
      onSuccess: (response) => {
        const queryKey = ['get_record_files', response?.report, response?.record_category_id]
        const recordFiles = (queryClient.getQueryData(queryKey) as IRecordFile[]) ?? []
        const updatedContent =
          recordFiles?.map((el) => {
            if (el?._id === response?._id) return response
            return el
          }) ?? []
        queryClient.setQueryData(queryKey, updatedContent)
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        const errorMessage = error?.response?.data?.message ?? 'Error while loading ZEH comments.'
        toast.error(`Failed to generate ZEH Comments: ${errorMessage}`)
      },
    },
  )
}

export const usePendingRecordFiles = (reportId: string, shouldFetch = true) => {
  const currentQueue = useRecordClassificationQueueStore((state) => state.queue)
  const importQueueFiles = useRecordClassificationQueueStore((state) => state.importPending)

  const importPendingToQueue = useCallback(
    (data: TRawRecordFileResponse[]) => {
      if (!data) return

      const filesToImport: QueueFile[] = []

      for (const pendingFile of data) {
        const currentClassification = pendingFile._metadata?.ai_classification_history
          ?.sort(
            dateComparator<IRecordFileClassificationHistoryRecord>({
              comparatorKey: 'started_at',
              ordenation: 'desc',
            }),
          )
          .at(0)

        if (!currentClassification) {
          continue
        }

        if (
          [
            'queue',
            'starting',
            'reading_file',
            'classifying_category',
            'classifying_file_class',
          ].includes(currentClassification.status) &&
          !currentQueue.some((qf) => qf.recordFileId === pendingFile._id)
        ) {
          filesToImport.push({
            id: pendingFile._id,
            recordFileId: pendingFile._id,
            classificationStatus: currentClassification.status,
            status: 'classifying',
            result: {
              category: {
                final: currentClassification.result?.record_category?.final_classification,
                suggestion:
                  currentClassification.result?.record_category?.new_classify_option_suggestion,
              },
              fileClass: {
                final: currentClassification.result?.file_class?.final_classification,
                suggestion:
                  currentClassification.result?.file_class?.new_classify_option_suggestion,
              },
            },
            uploadProgress: 100,
            file: new File([], pendingFile.file_name),
            reportId: pendingFile.report,
            isMoving: false,
            remoteReason: currentClassification.reason ?? undefined,
          })
        }
      }

      if (filesToImport) {
        importQueueFiles(filesToImport)
      }
    },
    [currentQueue, importQueueFiles],
  )

  return useQuery({
    queryKey: ['pending_record_files', reportId],
    queryFn: () => getPendingRecordFiles(reportId),
    staleTime: 5 * 60,
    enabled: shouldFetch,
    onSuccess: importPendingToQueue,
  })
}
